// @ts-nocheck
import { useState, useCallback } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { Button, Group, Select, Stack } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { SearchInput } from '../../components/SearchInput';
import { useEmployees } from '../../hooks/useEmployees';
import type { Employee } from '../../types/employee';

export default function EmployeeListPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [departmentFilter, setDepartmentFilter] = useState<string | null>(null);

  const { data, loading } = useEmployees({
    page,
    perPage: pageSize,
    search: search || undefined,
    status: statusFilter || undefined,
  });

  const handleSearch = useCallback((query: string) => {
    setSearch(query);
    setPage(1);
  }, []);

  const columns: Column<Employee>[] = [
    { key: 'employeeNumber', label: 'Employee #', sortable: true, width: 120 },
    {
      key: 'lastName',
      label: 'Name',
      sortable: true,
      render: (emp) => `${emp.lastName}, ${emp.firstName}`,
    },
    { key: 'departmentName', label: 'Department', sortable: true },
    { key: 'branchName', label: 'Branch', sortable: true },
    {
      key: 'status',
      label: 'Status',
      width: 120,
      render: (emp) => <StatusBadge status={emp.status} />,
    },
    {
      key: 'payType',
      label: 'Pay Type',
      width: 100,
      render: (emp) => emp.payType.charAt(0).toUpperCase() + emp.payType.slice(1),
    },
    {
      key: 'hireDate',
      label: 'Hire Date',
      sortable: true,
      width: 120,
      render: (emp) => new Date(emp.hireDate).toLocaleDateString(),
    },
  ];

  return (
    <Stack gap="md">
      <PageHeader
        title="Employees"
        subtitle={data ? `${data.length} employees` : undefined}
        actions={
          <Button icon={<IconPlus size={16} />} onClick={() => navigate('/admin/workforce/employees/new')}>
            Add Employee
          </Button>
        }
      />

      <Group gap="sm">
        <SearchInput onSearch={handleSearch} placeholder="Search employees..." />
        <Select
          placeholder="Status"
          clearable
          value={statusFilter}
          onChange={(value) => {
            setStatusFilter(value);
            setPage(1);
          }}
          data={[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'terminated', label: 'Terminated' },
            { value: 'on_leave', label: 'On Leave' },
          ]}
          w={160}
        />
        <Select
          placeholder="Department"
          clearable
          value={departmentFilter}
          onChange={(value) => {
            setDepartmentFilter(value);
            setPage(1);
          }}
          data={[
            // TODO: Populate from departments API
            { value: 'engineering', label: 'Engineering' },
            { value: 'sales', label: 'Sales' },
            { value: 'hr', label: 'Human Resources' },
            { value: 'operations', label: 'Operations' },
            { value: 'finance', label: 'Finance' },
          ]}
          w={180}
        />
      </Group>

      <DataTable<Record<string, unknown>>
        columns={columns as unknown as Column<Record<string, unknown>>[]}
        data={(data ?? []) as unknown as Record<string, unknown>[]}
        loading={loading}
        pagination={{
          page,
          pageSize,
          total: data?.length ?? 0,
          onPageChange: setPage,
        }}
        onRowClick={(emp) => navigate(`/employees/${(emp as unknown as Employee).id}`)}
        getRowKey={(emp) => (emp as unknown as Employee).id}
        emptyMessage="No employees found"
      />
    </Stack>
  );
}
